import React, { useEffect, useState } from "react";
import { MessageSquare, Languages, Mic, Users, Send, Loader2, TrendingUp, ArrowLeftRight, Clock } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Topbar } from "@/components/Topbar";

const DIR_COLORS = { "en->ar": "#10B981", "ar->en": "#0A4A3B" };
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-5" data-testid={`stat-${label}`}>
      <div className="h-10 w-10 rounded-xl bg-accent/10 text-accent grid place-items-center mb-3">
        <Icon size={20} />
      </div>
      <div className="font-head font-bold text-3xl text-foreground">{value}</div>
      <div className="text-sm text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function Panel({ title, icon: Icon, children, testid }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-5" data-testid={testid}>
      <h3 className="font-head font-semibold text-foreground mb-4 flex items-center gap-2">
        {Icon && <Icon size={16} className="text-accent" />} {title}
      </h3>
      {children}
    </div>
  );
}

function Heatmap({ cells }) {
  const map = {};
  let max = 0;
  cells.forEach((c) => {
    map[`${c.dow}-${c.hour}`] = c.count;
    if (c.count > max) max = c.count;
  });
  return (
    <div className="overflow-x-auto" data-testid="bi-heatmap-grid">
      <div className="min-w-[560px]">
        <div className="flex text-[10px] text-muted-foreground ps-9 mb-1">
          {HOURS.map((h) => (
            <div key={h} className="flex-1 text-center">{h % 3 === 0 ? h : ""}</div>
          ))}
        </div>
        {DOW.map((d, di) => (
          <div key={d} className="flex items-center mb-1">
            <div className="w-9 text-[11px] text-muted-foreground shrink-0">{d}</div>
            <div className="flex flex-1 gap-0.5">
              {HOURS.map((h) => {
                const v = map[`${di}-${h}`] || 0;
                const alpha = max ? 0.12 + (v / max) * 0.88 : 0;
                return (
                  <div
                    key={h}
                    title={`${d} ${h}:00 — ${v}`}
                    className="flex-1 aspect-square rounded-[3px]"
                    style={{ backgroundColor: v ? `rgba(16,185,129,${alpha})` : "#f1f5f4" }}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Analytics() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [bi, setBi] = useState(null);

  useEffect(() => {
    api.get("/analytics").then(({ data }) => setData(data)).catch(() => {});
    api.get("/analytics/bi").then(({ data }) => setBi(data)).catch(() => setBi(null));
  }, []);

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <Topbar />
        <div className="grid place-items-center py-32 text-primary"><Loader2 className="animate-spin" /></div>
      </div>
    );
  }

  const { totals, by_agent, by_day } = data;
  const maxPhrase = bi?.top_phrases?.[0]?.count || 1;

  return (
    <div className="min-h-screen bg-background">
      <Topbar />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="font-head font-bold text-2xl sm:text-3xl text-foreground mb-6">{t("analytics")}</h1>

        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          <StatCard icon={MessageSquare} label={t("totalConversations")} value={totals.conversations} />
          <StatCard icon={Send} label={t("totalMessages")} value={totals.messages} />
          <StatCard icon={Languages} label={t("totalTranslations")} value={totals.translations} />
          <StatCard icon={Mic} label={t("voiceTranslations")} value={totals.voice_translations} />
          <StatCard icon={Users} label={t("agentReplies")} value={totals.agent_replies} />
        </div>

        <div className="grid lg:grid-cols-2 gap-4">
          <Panel title={t("activityByAgent")}>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={by_agent}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="agent" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#10B981" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Panel>

          <Panel title={t("activityByDay")}>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#0A4A3B" strokeWidth={2.5} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </Panel>
        </div>

        {bi ? (
          <div className="mt-10" data-testid="bi-dashboard">
            <div className="flex items-center gap-3 mb-5">
              <h2 className="font-head font-bold text-xl text-foreground">{t("businessIntelligence")}</h2>
              <span className="text-sm text-muted-foreground">
                {t("biTotalTranslations")}: <b className="text-foreground">{bi.total_translations}</b>
              </span>
            </div>

            {bi.total_translations === 0 ? (
              <div className="bg-white border border-border rounded-2xl p-8 text-center text-muted-foreground text-sm" data-testid="bi-empty">
                {t("biNoData")}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid lg:grid-cols-2 gap-4">
                  <Panel title={t("biTopPhrases")} icon={TrendingUp} testid="bi-top-phrases">
                    {bi.top_phrases.length === 0 ? (
                      <p className="text-sm text-muted-foreground">{t("biNoPhrases")}</p>
                    ) : (
                      <div className="space-y-2.5">
                        {bi.top_phrases.map((p, i) => (
                          <div key={i} data-testid={`bi-phrase-${i}`}>
                            <div className="flex items-center justify-between gap-3 mb-1">
                              <span className="text-sm text-foreground truncate" dir="auto">{p.phrase}</span>
                              <span className="text-xs font-semibold text-muted-foreground shrink-0">{p.count}</span>
                            </div>
                            <div className="h-2 rounded-full bg-secondary overflow-hidden">
                              <div className="h-full rounded-full bg-accent" style={{ width: `${(p.count / maxPhrase) * 100}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Panel>

                  <Panel title={t("biDirections")} icon={ArrowLeftRight} testid="bi-directions">
                    <ResponsiveContainer width="100%" height={240}>
                      <PieChart>
                        <Pie data={bi.directions} dataKey="count" nameKey="direction" cx="50%" cy="50%" outerRadius={85} label={(e) => e.direction}>
                          {bi.directions.map((d, i) => (
                            <Cell key={i} fill={DIR_COLORS[d.direction] || "#94a3b8"} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </Panel>
                </div>

                <Panel title={t("biHeatmap")} icon={Clock} testid="bi-heatmap">
                  <Heatmap cells={bi.heatmap} />
                </Panel>

                <Panel title={t("biPerAgent")} icon={Users} testid="bi-per-agent">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-start text-muted-foreground border-b border-border">
                          <th className="text-start font-medium py-2">{t("agent")}</th>
                          <th className="text-end font-medium py-2">{t("biReplies")}</th>
                          <th className="text-end font-medium py-2">🇬🇧 EN</th>
                          <th className="text-end font-medium py-2">🇸🇦 AR</th>
                          <th className="text-end font-medium py-2">{t("biAvgResponse")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bi.per_agent.map((a, i) => (
                          <tr key={i} className="border-b border-border/60" data-testid={`bi-agent-${i}`}>
                            <td className="py-2.5 font-medium text-foreground">{a.agent}</td>
                            <td className="py-2.5 text-end">{a.replies}</td>
                            <td className="py-2.5 text-end">{a.en}</td>
                            <td className="py-2.5 text-end">{a.ar}</td>
                            <td className="py-2.5 text-end">{a.avg_response_min != null ? `${a.avg_response_min} ${t("biMinShort")}` : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
